// Kuratierte Lucide-Icon-Auswahl für Umfrage-Optionen.
// Wird sowohl im Admin-Panel (Icon-Picker) als auch in der Public-PollSection genutzt.
import {
  Rocket, Castle, Ship, Crown, Sparkles, TreePine, TrafficCone, Trophy,
  Candy, Lightbulb, Music, PartyPopper, Car, Cat, Palette, Waves, Flame,
  Star, Heart, Gamepad2, Plane, Bot, Snowflake, Bird, Ghost, PawPrint,
  Truck, Bug, Zap, Fish, Moon, Sun, Rainbow, Gem, Tent, Mountain,
  Gift, IceCream, Cake, Cookie,
} from 'lucide-react';

// Reihenfolge = Reihenfolge im Icon-Picker. Namen bewusst kurz und Screenshot-orientiert.
export const POLL_ICONS = [
  { name: 'Rocket',      label: 'Weltraum / UFO',        Icon: Rocket },
  { name: 'Plane',       label: 'Flugzeug / Flughafen',  Icon: Plane },
  { name: 'Car',         label: 'Auto / Rennwagen',      Icon: Car },
  { name: 'Truck',       label: 'Monster Truck',         Icon: Truck },
  { name: 'Ship',        label: 'Piraten / Schiff',      Icon: Ship },
  { name: 'Castle',      label: 'Ritter / Burg',         Icon: Castle },
  { name: 'Crown',       label: 'Prinzessin / König',    Icon: Crown },
  { name: 'Sparkles',    label: 'Magie / Einhorn',       Icon: Sparkles },
  { name: 'Star',        label: 'Stars / Show',          Icon: Star },
  { name: 'TreePine',    label: 'Dschungel / Safari',    Icon: TreePine },
  { name: 'Tent',        label: 'Camp / Zelt',           Icon: Tent },
  { name: 'Mountain',    label: 'Berge / Abenteuer',     Icon: Mountain },
  { name: 'Cat',         label: 'Katzen / Tiere',        Icon: Cat },
  { name: 'PawPrint',    label: 'Bauernhof / Pfote',     Icon: PawPrint },
  { name: 'Bird',        label: 'Vogel / Flamingo',      Icon: Bird },
  { name: 'Fish',        label: 'Fisch / Meer',          Icon: Fish },
  { name: 'Waves',       label: 'Wasser / Meer',         Icon: Waves },
  { name: 'Snowflake',   label: 'Eiswelt / Pinguin',     Icon: Snowflake },
  { name: 'Flame',       label: 'Feuer / Drachen',       Icon: Flame },
  { name: 'Ghost',       label: 'Monster / Halloween',   Icon: Ghost },
  { name: 'Bot',         label: 'Roboter',               Icon: Bot },
  { name: 'Bug',         label: 'Insekten',              Icon: Bug },
  { name: 'TrafficCone', label: 'Baustelle',             Icon: TrafficCone },
  { name: 'Trophy',      label: 'Sport / Fußball',       Icon: Trophy },
  { name: 'Gamepad2',    label: 'Gaming',                Icon: Gamepad2 },
  { name: 'Music',       label: 'Musik / Party',         Icon: Music },
  { name: 'PartyPopper', label: 'Party',                 Icon: PartyPopper },
  { name: 'Palette',     label: 'Kreativ / Malen',       Icon: Palette },
  { name: 'Rainbow',     label: 'Regenbogen',            Icon: Rainbow },
  { name: 'Sun',         label: 'Sonne',                 Icon: Sun },
  { name: 'Moon',        label: 'Mond / Nacht',          Icon: Moon },
  { name: 'Zap',         label: 'Superhelden / Blitz',   Icon: Zap },
  { name: 'Heart',       label: 'Liebe / Herz',          Icon: Heart },
  { name: 'Gem',         label: 'Diamant / Schatz',      Icon: Gem },
  { name: 'Gift',        label: 'Geschenk',              Icon: Gift },
  { name: 'Candy',       label: 'Süßes / Candy',         Icon: Candy },
  { name: 'IceCream',    label: 'Eis / Sommer',          Icon: IceCream },
  { name: 'Cake',        label: 'Torte / Geburtstag',    Icon: Cake },
  { name: 'Cookie',      label: 'Kekse / Cookies',       Icon: Cookie },
  { name: 'Lightbulb',   label: 'Ganz neue Idee',        Icon: Lightbulb },
];

const MAP = Object.fromEntries(POLL_ICONS.map((i) => [i.name, i.Icon]));

// Keyword-basierte Auto-Erkennung: greift wenn kein `icon` gesetzt ist.
// Reihenfolge = Priorität (spezifische Matches zuerst).
const KEYWORD_MAP = [
  [/monster.?truck|truck/i,               'Truck'],
  [/rennwagen|rennauto|motorsport|formel|auto/i, 'Car'],
  [/flugzeug|flughafen|jet|helikopter|flug/i,     'Plane'],
  [/rakete|weltraum|space|ufo|alien/i,   'Rocket'],
  [/piraten|schiff|boot|meer|ozean/i,    'Ship'],
  [/ritter|burg|schloss|castle/i,        'Castle'],
  [/prinzess|feen|fee\b/i,               'Crown'],
  [/einhorn|magie|zauber/i,              'Sparkles'],
  [/dschungel|safari|urwald|jungle/i,    'TreePine'],
  [/farm|bauernhof|tier|pferd|ranch|traktor/i, 'PawPrint'],
  [/flamingo|vogel|papagei|bird/i,       'Bird'],
  [/eis|pinguin|arktis|nordpol|winter|schnee/i, 'Snowflake'],
  [/monster|geist|halloween|zombie/i,    'Ghost'],
  [/roboter|robot|android/i,             'Bot'],
  [/panda|bär|lion|löwe|tiger|zoo/i,     'PawPrint'],
  [/dinosaur|dino|drache/i,              'Flame'],
  [/insekt|biene|schmetterling|marien/i, 'Bug'],
  [/baustelle|bagger|kran/i,             'TrafficCone'],
  [/sport|fussball|fußball|basketball|trophäe/i, 'Trophy'],
  [/gaming|game|konsole|controller/i,    'Gamepad2'],
  [/music|musik|band|dj/i,               'Music'],
  [/party|disco/i,                       'PartyPopper'],
  [/kreativ|malen|kunst|zeichnen/i,      'Palette'],
  [/regenbogen|rainbow/i,                'Rainbow'],
  [/sonne|summer|sommer/i,               'Sun'],
  [/mond|nacht|dunkel/i,                 'Moon'],
  [/superheld|blitz|zap|hero/i,          'Zap'],
  [/herz|love|liebe/i,                   'Heart'],
  [/diamant|schatz|edelstein|juwel|gem/i,'Gem'],
  [/geschenk|gift/i,                     'Gift'],
  [/süß|candy|zucker|lolli/i,            'Candy'],
  [/eiscreme|ice.?cream|eisdiele|slush/i,'IceCream'],
  [/torte|geburtstag|cake|kuchen/i,      'Cake'],
  [/keks|cookie|biscuit/i,               'Cookie'],
  [/fisch|hai|delfin|wal/i,              'Fish'],
  [/wasser|welle|pool/i,                 'Waves'],
  [/berg|klettern|mountain/i,            'Mountain'],
  [/zelt|camping|tent/i,                 'Tent'],
  [/star|show|hollywood|promi/i,         'Star'],
];

const detectByKeyword = (label) => {
  if (!label) return null;
  for (const [rx, name] of KEYWORD_MAP) {
    if (rx.test(label)) return MAP[name];
  }
  return null;
};

// Sicheres Auflösen: explizit gesetztes Icon → Keyword-Match am Label → Fallback Sparkles.
// „Sparkles" wird als Standard-Platzhalter behandelt (nicht als bewusste Wahl),
// damit die Keyword-Erkennung auch bei Bestandsdaten greift, die alle den
// Default-Sparkles-Icon-Namen haben.
export const resolvePollIcon = (name, label) => {
  if (name && name !== 'Sparkles' && MAP[name]) return MAP[name];
  const auto = detectByKeyword(label);
  if (auto) return auto;
  return Sparkles;
};
